import { BlockedTime } from 'src/common/providers/useBookingMotivators';
import { TripCartPrice } from 'src/common/types';
import { TripCartSeasonData } from 'src/listing/types';

// ======================================================================
// Tiers
// ======================================================================

// search params
type TierCacheKey = {
  captainOption: string;
  guests: string;
  currency: string;
  duration: string;
  date: string;
};

export type TierCacheValue = TripCartPrice;

const tierCache: Map<string, TierCacheValue> = new Map();

// Compression isn't super important, as long as it's unique
const encodeTierKey = ({
  captainOption, guests, currency, duration, date,
}: TierCacheKey) => `dt:${date},d:${duration},g:${guests},c:${currency},o:${captainOption}`;

export const getCachedTierPrices = (key: TierCacheKey): TierCacheValue | null => {
  const cacheKey = encodeTierKey(key);
  if (tierCache.has(cacheKey)) {
    return tierCache.get(cacheKey)!;
  }

  return null;
};

export const setCachedTierPrices = (key: TierCacheKey, value: TierCacheValue): void => {
  const cacheKey = encodeTierKey(key);
  tierCache.set(cacheKey, value);
};

// ======================================================================
// Blocked Time
// ======================================================================

type BlockedTimeCacheKey = {
  date: string;
};
type BlockedTimeCacheValue = BlockedTime[];

const blockedTimeCache: Map<string, BlockedTimeCacheValue> = new Map();

const encodeBlockedTimeKey = ({
  date,
}: BlockedTimeCacheKey) => `d:${date}`;

export const getCachedBlockedTimes = (key: BlockedTimeCacheKey): BlockedTimeCacheValue | null => {
  const cacheKey = encodeBlockedTimeKey(key);
  if (blockedTimeCache.has(cacheKey)) {
    return blockedTimeCache.get(cacheKey)!;
  }

  return null;
};

export const setCachedBlockedTimes = (
  key: BlockedTimeCacheKey,
  value: BlockedTimeCacheValue,
): void => {
  const cacheKey = encodeBlockedTimeKey(key);
  blockedTimeCache.set(cacheKey, value);
};

// ======================================================================
// Seasons
// ======================================================================

type SeasonCacheKey = {
  date: string;
};

type SeasonCacheValue = TripCartSeasonData;

const seasonCache: Map<string, SeasonCacheValue> = new Map();

const encodeSeasonKey = ({ date }: SeasonCacheKey) => `d:${date}`;

export const getCachedSeasonForDate = (key: SeasonCacheKey): SeasonCacheValue | null => {
  const cacheKey = encodeSeasonKey(key);
  if (seasonCache.has(cacheKey)) {
    return seasonCache.get(cacheKey)!;
  }
  return null;
};

export const setCachedSeasonForDate = (key: SeasonCacheKey, value: SeasonCacheValue): void => {
  const cacheKey = encodeSeasonKey(key);
  seasonCache.set(cacheKey, value);
};

// ======================================================================
// Clear
// ======================================================================

export const clearCaches = (): void => {
  tierCache.clear();
  blockedTimeCache.clear();
};
